import { Page } from "../../../components/Page";
import { Card } from "../../../components/Card";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { DataTable } from "../../../components/DataTable";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { MultiSelectFilter } from "../../../components/DataTable/components";
import {
  formatDate,
  getSortDirection,
  showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS,
  USER_STATUSES,
  USER_TYPES
} from "../../../utils";
import { FaBars, FaEnvelope, FaPen, FaPlay, FaTimes } from "react-icons/fa";
import { AddExternalUserModal, UserModal } from "./components";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import {
  useActivateUser,
  useAddExternalUser,
  useCreateUser,
  useDeactivateUser,
  useGetUsers,
  useGetUsersRoles,
  useGetUsersStatuses,
  userAdministrationKeys,
  useResendInvitation,
  useUpdateUser
} from "../../../hooks/query/user-administration";
import { WithAuthorization } from "../../../context";
import useAirUserRoles from "utils/AuthUtils";

/**
 * table of users
 */
function Users() {
  const { t } = useTranslation("translation", {
    keyPrefix: "userManagement"
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [usersList, setUsersList] = useState([]);
  // Note: this is _almost_ the user object -- but with roles changed to [{label, value:id}] TODO refactor to use the actual user object
  const [userModalData, setUserModalData] = useState(undefined);
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const {
    isOpen: isExternalUserModalOpen,
    onOpen: onExternalUserModalOpen,
    onClose: onExternalUserModalClose
  } = useDisclosure();
  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`activeStatus,${SORT_DIRECTIONS.ASC}`, `username,${SORT_DIRECTIONS.ASC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const { totalPages, filterObj, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const calcState = (prevState) => {
      let changedAttributes = {};

      Object.keys(newValObject).forEach(dataTableAttribute => {
        const value = newValObject[dataTableAttribute];

        if (dataTableAttribute === "filterObj") {
          // We have to save not only the new value sent from the data table
          // but also the previously applied filters, which we get from prevState
          changedAttributes[dataTableAttribute] = {
            ...prevState[dataTableAttribute],
            [value.attribute]: value.value
          };
        } else if (dataTableAttribute === "queryPageSortBy") {
          let stringValue = value[0];
          let accessorName = stringValue.split(",")[0];
          const sortDirection = stringValue.split(",")[1];
          const updatedArray = [...prevState[dataTableAttribute]];

          if (accessorName === "status") {
            // Back-end requires the usage of 'activeStatus' instead of 'status' for sorting
            stringValue = `activeStatus,${sortDirection}`;
            accessorName = "activeStatus";
          } else if (accessorName === "roles") {
            // Back-end requires the usage of 'userRoles' instead of 'roles' for sorting
            stringValue = `userRoles,${sortDirection}`;
            accessorName = "userRoles";
          }

          const indexOfExisting = updatedArray.findIndex(el => {
            return el.split(",")[0] === accessorName;
          });
          if (indexOfExisting > -1) {
            // Column which we try to sort is already in the sort list, so we just manipulate it's record
            if (sortDirection === SORT_DIRECTIONS.NONE) {
              updatedArray.splice(indexOfExisting, 1);
            } else {
              updatedArray[indexOfExisting] = stringValue;
            }
          } else {
            // Column which we try to sort does not exist in the sort list, so we add it
            updatedArray.push(stringValue);
          }

          changedAttributes[dataTableAttribute] = updatedArray;
        } else {
          // All other values have primitive types, so we apply them directly
          // without checking their previous states
          changedAttributes[dataTableAttribute] = value;
        }
      });

      return changedAttributes;
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calcState(prevState)
      };
    });
  };

  const constructQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    // "status", "roles" and "sort" params are extracted from queryParams object, because they can have more than one value.
    // To avoid overriding of those multiple values which have to share the same key ("status", "roles" or "sort"), we put them
    // together in a string called "multipleValuesParams"
    let multipleValuesParams = "";
    Object.keys(queryParams).forEach(param => {
      if (queryParams[param] === "" || queryParams[param].length === 0) {
        // Removing empty params
        delete queryParams[param];
      } else if (param === "status") {
        queryParams[param].forEach(statusObj => {
          multipleValuesParams += `&statuses=${statusObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "roles") {
        queryParams[param].forEach(roleObj => {
          multipleValuesParams += `&roleIds=${roleObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "sort") {
        queryParams[param].forEach(sortStr => {
          multipleValuesParams += `&sort=${sortStr}`;
        });
        delete queryParams[param];
      }
    });

    return `${new URLSearchParams(queryParams).toString()}${multipleValuesParams}`;
  };

  const {
    data: usersStatuses
  } = useGetUsersStatuses({
    userType: USER_TYPES.ORG_MEMBER
  }, {
    onError: err => handleErrors(err)
  });

  const {
    data: usersRoles // [{id:number, role:string}]
  } = useGetUsersRoles({
    userType: USER_TYPES.ORG_MEMBER
  }, {
    onError: err => handleErrors(err)
  });
  // No UI to set roles the current user can't manage (see also: backend security)
  let safeUserRoles = usersRoles?.roles || [];
  const { isOrgAdmin, isESGAdmin } = useAirUserRoles();
  if (!isOrgAdmin) {
    safeUserRoles = safeUserRoles.filter(role => role.role !== "ORG_ADMIN");
  }
  if (!isESGAdmin) {
    safeUserRoles = safeUserRoles.filter(role => role.role !== "ESG_ADMIN");
  }
//   console.log("safeUserRoles", safeUserRoles, "usersRoles", usersRoles);

  const {
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers
  } = useGetUsers(constructQueryParamsString(),
    {
      onSuccess: (data) => {
        const { page } = data;
        const pageContent = page?.content ?? [];
        const users = pageContent.map((user) => {
          return {
            id: user?.id,
            username: user?.username,
            firstName: user?.firstName,
            lastName: user?.lastName,
            roles: user?.roles,
            status: user?.status,
            lastLogin: formatDate(user?.lastLogin ?? null)
          };
        });
        setUsersList(users);
        if (users.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => handleErrors(err),
      cacheTime: 0,
      enabled: usersStatuses !== null && typeof usersStatuses !== "undefined" && usersRoles !== null &&
        typeof usersRoles !== "undefined"
    }
  );

  const handleErrors = (error) => {
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    if (error.response?.status === 409 && error.response?.data?.errorCode === "object-conflict") {
      errorMessage = t("messages.duplicatedUsername");
    } else if (error.response?.status === 404 && error.response?.data?.parameters?.resourceName === "User") {
      errorMessage = t("messages.userDoesNotExists");
    }

    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const { mutate: createUser } = useCreateUser({
    onSuccess: () => {
      closeUserModal();
      showSuccessToast(toast, t("messages.createdUser"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: addExternalUser } = useAddExternalUser({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      onExternalUserModalClose();
      showSuccessToast(toast, t("messages.addedExternalUser"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: () => {
      closeUserModal();
      showSuccessToast(toast, t("messages.updatedUser"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: resendInvitation } = useResendInvitation({
    onSuccess: () => {
      showSuccessToast(toast, t("messages.invitationSent"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: activateUser } = useActivateUser({
    onSuccess: () => {
      showSuccessToast(toast, t("messages.activatedUser"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: deactivateUser } = useDeactivateUser({
    onSuccess: () => {
      showSuccessToast(toast, t("messages.deactivatedUser"));
      queryClient.invalidateQueries(userAdministrationKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const columns = useMemo(() => {
      function handleResendInvitation(data) {
        dispatch(showAppSpinner());
        resendInvitation(data?.row?.original?.id);
      }

      function handleManageUser(data) {
        const mappedData = {
          ...data.row.original,
          roles: data.row.original.roles.map(roleObj => {
            return {
              label: t(`roles.${roleObj?.role}`, { defaultValue: roleObj?.role }),
              value: roleObj?.id
            };
          })
        };

        setUserModalData(mappedData);
        onUserModalOpen();
      }

      function handleActivateUser(data) {
        dispatch(showAppSpinner());
        activateUser(data?.row?.original?.id);
      }

      function handleDeactivateUser(data) {
        dispatch(showAppSpinner());
        deactivateUser(data?.row?.original?.id);
      }

      return [
        {
          header: t("gridHeaders.username"),
          accessorKey: "username",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "username")
        },
        {
          header: t("gridHeaders.firstName"),
          accessorKey: "firstName",
          size: 140,
          sortDirection: getSortDirection(queryPageSortBy, "firstName")
        },
        {
          header: t("gridHeaders.lastName"),
          accessorKey: "lastName",
          size: 140,
          sortDirection: getSortDirection(queryPageSortBy, "lastName")
        },
        {
          header: t("gridHeaders.userRole"),
          accessorKey: "roles",
          size: 300,
          isSortable: false,
          filter: {
            params: {
              options: usersRoles && usersRoles.roles?.length > 0 ?
                usersRoles.roles.map(roleObj => {
                  return {
                    label: t(`roles.${roleObj?.role}`, { defaultValue: roleObj?.role }),
                    value: roleObj?.id
                  };
                }) : [],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          },
          cell: cell => (
            <Box>
              {cell.row.original?.roles?.map(roleObj => t(`roles.${roleObj.role}`)).join(", ") ?? ""}
            </Box>
          )
        },
        {
          header: t("gridHeaders.userStatus"),
          accessorKey: "status",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "activeStatus"),
          filter: {
            params: {
              options: usersStatuses && usersStatuses.statuses?.length > 0 ?
                usersStatuses.statuses.map(el => {
                  return {
                    label: t(`statuses.${el}`, { defaultValue: el }),
                    value: el
                  };
                }) : [],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          },
          cell: cell => (
            <Badge
              backgroundColor={cell.row.original.status === USER_STATUSES.ACTIVE ? "green.100" : "red.100"}
              color={cell.row.original.status === USER_STATUSES.ACTIVE ? "green.800" : "red.800"}
              height="max-content"
            >
              {cell.row.original.status}
            </Badge>
          )
        },
        {
          header: t("gridHeaders.lastLogin"),
          accessorKey: "lastLogin",
          size: 120,
          enableColumnFilter: false,
          isSortable: false
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 80,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  isDisabled={row.row.original.status === USER_STATUSES.DEACTIVATED}
                  icon={<FaEnvelope color="#6EA161" />}
                  onClick={() => handleResendInvitation(row)}
                >
                  {t("buttons.resendInvitation")}
                </MenuItem>
                <MenuItem
                  isDisabled={row.row.original.status !== USER_STATUSES.ACTIVE}
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleManageUser(row)}
                >
                  {t("buttons.manageUser")}
                </MenuItem>
                {row.row.original.status === USER_STATUSES.DEACTIVATED && <MenuItem
                  icon={<FaPlay color="#6EA161" />}
                  onClick={() => handleActivateUser(row)}
                >
                  {t("buttons.activateUser")}
                </MenuItem>}
                {row.row.original.status === USER_STATUSES.ACTIVE &&
                  <MenuItem
                    icon={<FaTimes color="#6EA161" />}
                    onClick={() => handleDeactivateUser(row)}
                  >
                    {t("buttons.deactivateUser")}
                  </MenuItem>}
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, filterObj, onUserModalOpen, usersStatuses, usersRoles, resendInvitation, activateUser,
      deactivateUser, dispatch]
  );

  const handleAddNewUser = () => {
    onUserModalOpen();
  };

  const handleAddExternalUser = () => {
    onExternalUserModalOpen();
  };

  const closeUserModal = () => {
    setUserModalData(undefined);
    onUserModalClose();
  };

  const handleUserModalSubmit = (id, data) => {
    dispatch(showAppSpinner());

    if (id !== undefined) {
      updateUser({ id, data });
    } else {
      createUser(data);
    }
  };

  const handleExternalUserModalSubmit = (data) => {
    addExternalUser(data)
  };

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <Box marginBlockStart="5">
          <Flex marginBlockEnd="5" gap={2}>
            <Button
              colorScheme="customScheme"
              width="fit-content"
              onClick={handleAddNewUser}
            >
              + {t("buttons.addNewUser")}
            </Button>
            <Button
              colorScheme="customScheme"
              width="fit-content"
              onClick={handleAddExternalUser}
            >
              + {t("buttons.addExternalUser")}
            </Button>
          </Flex>

          {isUserModalOpen && usersRoles && <UserModal
            modalData={userModalData}
            isOpen={isUserModalOpen}
            onClose={closeUserModal}
            onSubmit={handleUserModalSubmit}
			safeRoles={safeUserRoles}
          />}

          {isExternalUserModalOpen && usersRoles && <AddExternalUserModal
            isOpen={isExternalUserModalOpen}
            onClose={onExternalUserModalClose}
            onSubmit={handleExternalUserModalSubmit}
            roles={usersRoles.roles}
          />}

          <DataTable
            data={usersList}
            columns={columns}
            isLoading={isLoadingUsers || isFetchingUsers}
            serverSidePagination={true}
            queryTotalCount={totalElements}
            queryPageCount={totalPages}
            queryPageIndex={queryPageIndex}
            setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
            queryPageSize={queryPageSize}
            setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
            queryPageSortBy={queryPageSortBy}
            setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
            filterObj={filterObj}
            setFilterObj={(newFilterObj) => manageDataTableState({
              filterObj: newFilterObj,
              queryPageIndex: 0
            })}
            enableFilters={true}
            enableSorting={true}
          />
        </Box>
      </Card>
    </Page>
  );
}

export default WithAuthorization(Users);